import React, { useState, useEffect, useMemo } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Download, Printer, X, ShieldAlert, Copy, Search, CheckCircle2, XCircle, CheckSquare, Square, Loader2 } from 'lucide-react';
import MonthFilter, { getMonthDateRange } from '../components/MonthFilter';
import UnsavedChangesDialog, { useUnsavedChanges } from '../components/UnsavedChangesDialog';
import { useVisibility } from '../contexts/VisibilityContext';

// Cores dos postos (mesmo do Dashboard)
const POSTO_COLORS = {
  'POSTO JP&HG LOBO': { bg: '#10B981', text: 'white' },
  'POSTO CARAUARI': { bg: '#EF4444', text: 'white' },
  'REDE BONS AMIGOS': { bg: '#3B82F6', text: 'white' },
  'PONTÃO BARROTE': { bg: '#6B7280', text: 'white' },
};

export default function AbastecimentosPage() {
  const { isAdmin } = useAuth();
  const [abastecimentos, setAbastecimentos] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const [veiculos, setVeiculos] = useState([]);
  const [postos, setPostos] = useState([]);
  const [combustiveis, setCombustiveis] = useState([]);
  const [secretarias, setSecretarias] = useState([]);
  const [comunidades, setComunidades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightId, setHighlightId] = useState(null);
  const [caError, setCaError] = useState('');
  const [caChecking, setCaChecking] = useState(false);
  const [selectedForPrint, setSelectedForPrint] = useState(new Set());
  const [printingBatch, setPrintingBatch] = useState(false);
  
  // Filtro por mês - começa no mês atual
  const currentMonth = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  
  // Hook para controle de alterações não salvas
  const unsavedChanges = useUnsavedChanges();
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    secretaria_id: '',
    funcionario_id: '',
    veiculo_id: '',
    posto_id: '',
    combustivel_id: '',
    quantidade: '',
    preco_unitario: '',
    odometer_start: '',
    odometer_end: '',
    codigo_abastecimento: '',
    comunidade_id: '',
    motor_hp: '',
    observacoes: '',
    servico: ''
  });
  const [items, setItems] = useState([]);

  // Detect SEMSA-RESGATE
  const isResgate = useMemo(() => {
    if (!formData.secretaria_id) return false;
    const sec = secretarias.find(s => s.id === formData.secretaria_id);
    return sec && sec.name && sec.name.toUpperCase().includes('RESGATE');
  }, [formData.secretaria_id, secretarias]);

  // Filter funcionarios by selected secretaria
  const funcionariosFiltrados = formData.secretaria_id 
    ? funcionarios.filter(f => f.secretaria_id === formData.secretaria_id)
    : [];

  // Filter veiculos by selected secretaria + include vehicle linked to selected funcionario + include currently selected vehicle
  const veiculosFiltrados = React.useMemo(() => {
    if (!formData.secretaria_id) return [];
    
    const funcionarioSelecionado = formData.funcionario_id 
      ? funcionarios.find(f => f.id === formData.funcionario_id) 
      : null;
    
    return veiculos.filter(v => {
      // Incluir veiculos da secretaria OU o veiculo vinculado ao funcionario selecionado OU o veiculo atualmente selecionado (do histórico)
      return v.secretaria_id === formData.secretaria_id || 
             (funcionarioSelecionado && v.id === funcionarioSelecionado.veiculo_id) ||
             (formData.veiculo_id && v.id === formData.veiculo_id);
    });
  }, [formData.secretaria_id, formData.funcionario_id, formData.veiculo_id, funcionarios, veiculos]);

  // Filter combustiveis by selected posto
  const combustiveisFiltrados = formData.posto_id 
    ? combustiveis.filter(c => c.posto_id === formData.posto_id)
    : [];

  // Buscar último veículo usado por um funcionário no histórico de abastecimentos
  const getLastUsedVehicle = React.useCallback((funcId) => {
    if (!funcId) return null;
    // 1) Verificar se o funcionário tem veiculo_id direto no cadastro
    const func = funcionarios.find(f => f.id === funcId);
    if (func && func.veiculo_id) return func.veiculo_id;
    // 2) Buscar nos itens dos abastecimentos mais recentes
    for (const abast of abastecimentos) {
      if (abast.items) {
        for (const item of abast.items) {
          if (item.funcionario_id === funcId && item.veiculo_id) {
            return item.veiculo_id;
          }
        }
      }
      // Checar nível principal do abastecimento
      if (abast.funcionario_id === funcId && abast.veiculo_id) {
        return abast.veiculo_id;
      }
    }
    // 3) Se a secretaria tem apenas 1 veículo, usar esse
    if (func && func.secretaria_id) {
      const veicsSec = veiculos.filter(v => v.secretaria_id === func.secretaria_id);
      if (veicsSec.length === 1) return veicsSec[0].id;
    }
    return null;
  }, [funcionarios, abastecimentos, veiculos]);

  // Auto-preencher valor do litro quando selecionar combustível
  const handleCombustivelChange = (combustivelId) => {
    const comb = combustiveis.find(c => c.id === combustivelId);
    setFormData({ 
      ...formData, 
      combustivel_id: combustivelId,
      preco_unitario: comb?.valor_litro?.toString() || formData.preco_unitario
    });
  };

  // Limpar combustível quando trocar de posto
  const handlePostoChange = (postoId) => {
    setFormData({ 
      ...formData, 
      posto_id: postoId,
      combustivel_id: '',
      preco_unitario: ''
    });
  };

  useEffect(() => {
    if (isAdmin()) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Suporte a ?edit=ID vindo de redirect (SecretariaDashboard)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const editId = params.get('edit');
    if (editId && abastecimentos.length > 0) {
      const item = abastecimentos.find(a => a.id === editId);
      if (item) {
        handleEdit(item);
        // Limpar URL
        window.history.replaceState({}, '', '/abastecimentos');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abastecimentos]);

  // Filtrar abastecimentos - quando pesquisa ativa, busca em TODOS os registros
  const abastecimentosFiltrados = useMemo(() => {
    let filtered = abastecimentos;
    // Filtro por barra de pesquisa (CA, data, secretaria) - ignora filtro de mês
    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      filtered = filtered.filter(a => {
        const ca = (a.codigo_abastecimento || '').toString().toLowerCase();
        const data = a.date ? a.date.split('-').reverse().join('/') : '';
        const func = funcionarios.find(f => f.id === a.funcionario_id);
        const secId = func?.secretaria_id;
        const secNome = secId ? (secretarias.find(s => s.id === secId)?.name || '') : '';
        const comunidade = a.comunidade_id ? 'resgate' : '';
        return ca.includes(term) || data.includes(term) || secNome.toLowerCase().includes(term) || comunidade.includes(term);
      });
    } else if (selectedMonth) {
      // Sem pesquisa: aplica filtro de mês normalmente
      const { startDate, endDate } = getMonthDateRange(selectedMonth);
      filtered = filtered.filter(a => {
        if (!a.date) return false;
        return a.date >= startDate && a.date <= endDate;
      });
    }
    return filtered.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  }, [abastecimentos, selectedMonth, searchTerm, funcionarios, secretarias]);

  // Verificação de permissão - apenas Admin pode acessar esta página
  if (!isAdmin()) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <ShieldAlert className="mx-auto h-16 w-16 text-red-500 mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Acesso Restrito</h2>
            <p className="text-gray-600 mb-4">
              Apenas <strong>Administradores</strong> podem gerenciar abastecimentos.
            </p>
            <p className="text-sm text-gray-500">
              Se você precisa registrar um abastecimento, entre em contato com um administrador do sistema.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const fetchData = async () => {
    try {
      // Buscar TODOS abastecimentos (filtro de mês é feito no client)
      const [abastecimentosRes, funcionariosRes, veiculosRes, postosRes, combustiveisRes, secretariasRes, comunidadesRes] = await Promise.all([
        api.get('/abastecimentos'),
        api.get('/funcionarios'),
        api.get('/veiculos'),
        api.get('/postos'),
        api.get('/combustiveis'),
        api.get('/secretarias'),
        api.get('/comunidades')
      ]);
      setAbastecimentos(abastecimentosRes.data);
      setFuncionarios(funcionariosRes.data);
      setVeiculos(veiculosRes.data);
      setPostos(postosRes.data);
      setCombustiveis(combustiveisRes.data);
      setSecretarias(secretariasRes.data);
      setComunidades(comunidadesRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    setItems([...items, { combustivel_id: '', quantity: '', price_per_liter: '', funcionario_id: '', veiculo_id: '' }]);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Bloquear se CA duplicada
    if (caError) {
      toast.error('ESSE Nº DE CA JÁ EXISTE! Altere antes de salvar.');
      return;
    }

    if (!isResgate && parseFloat(formData.odometer_end) < parseFloat(formData.odometer_start)) {
      toast.error('Hodômetro final deve ser maior que o inicial');
      return;
    }

    // Validate main form fields for combustivel
    if (!formData.combustivel_id || !formData.quantidade || !formData.preco_unitario) {
      toast.error('Preencha o tipo de combustível, quantidade e preço');
      return;
    }

    // Create the first item from the main form data
    const firstItem = {
      combustivel_id: formData.combustivel_id,
      quantity: parseFloat(formData.quantidade),
      price_per_liter: parseFloat(formData.preco_unitario),
      funcionario_id: formData.funcionario_id,
      veiculo_id: isResgate ? '' : formData.veiculo_id,
      total_value: 0
    };

    // Add extra items if any were filled (filter out empty ones)
    const extraItems = items
      .filter(item => item.combustivel_id && item.quantity && item.price_per_liter && item.funcionario_id)
      .map(item => ({
        combustivel_id: item.combustivel_id,
        quantity: parseFloat(item.quantity),
        price_per_liter: parseFloat(item.price_per_liter),
        funcionario_id: item.funcionario_id,
        veiculo_id: isResgate ? '' : (item.veiculo_id || formData.veiculo_id),
        total_value: 0
      }));

    // Combine first item + extra items
    const allItems = [firstItem, ...extraItems];

    const submitData = {
      date: formData.date,
      funcionario_id: formData.funcionario_id,
      veiculo_id: isResgate ? '' : formData.veiculo_id,
      posto_id: formData.posto_id,
      invoice_number: formData.codigo_abastecimento || 'N/A',
      odometer_start: isResgate ? 0 : parseFloat(formData.odometer_start || 0),
      odometer_end: isResgate ? 0 : parseFloat(formData.odometer_end || 0),
      codigo_abastecimento: formData.codigo_abastecimento,
      comunidade_id: isResgate ? formData.comunidade_id : null,
      motor_hp: isResgate ? formData.motor_hp : null,
      observacoes: formData.observacoes || null,
      servico: formData.servico || null,
      items: allItems
    };

    try {
      let savedId = null;
      if (editingId) {
        await api.put(`/abastecimentos/${editingId}`, submitData);
        savedId = editingId;
        toast.success('Abastecimento atualizado com sucesso!');
      } else {
        const res = await api.post('/abastecimentos', submitData);
        savedId = res.data?.id || null;
        toast.success('Abastecimento registrado com sucesso!');
      }
      setDialogOpen(false);
      resetForm();
      await fetchData();
      // Scroll to saved row and flash it
      if (savedId) {
        setHighlightId(savedId);
        setTimeout(() => {
          const row = document.querySelector(`[data-testid="abastecimento-row-${savedId}"]`);
          if (row) {
            row.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 300);
        setTimeout(() => setHighlightId(null), 3000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar abastecimento');
    }
  };

  const handleEdit = (item) => {
    // Find the secretaria_id from the funcionario
    const func = funcionarios.find(f => f.id === item.funcionario_id);
    const secretariaId = func?.secretaria_id || '';
    
    // Get combustivel_id and other data from first item if exists
    const firstItem = item.items && item.items.length > 0 ? item.items[0] : null;
    
    setFormData({
      date: item.date,
      secretaria_id: secretariaId,
      funcionario_id: item.funcionario_id,
      veiculo_id: item.veiculo_id || '',
      posto_id: item.posto_id,
      combustivel_id: firstItem?.combustivel_id || '',
      quantidade: firstItem?.quantity?.toString() || '',
      preco_unitario: firstItem?.price_per_liter?.toString() || '',
      odometer_start: (item.odometer_start || 0).toString(),
      odometer_end: (item.odometer_end || 0).toString(),
      codigo_abastecimento: item.codigo_abastecimento || '',
      comunidade_id: item.comunidade_id || '',
      motor_hp: item.motor_hp || '',
      observacoes: item.observacoes || '',
      servico: item.servico || ''
    });
    
    // Apenas itens adicionais (do segundo em diante) - o primeiro já está no formulário principal
    const additionalItems = item.items && item.items.length > 1 
      ? item.items.slice(1).map(it => ({
          combustivel_id: it.combustivel_id || '',
          quantity: it.quantity?.toString() || '',
          price_per_liter: it.price_per_liter?.toString() || '',
          funcionario_id: it.funcionario_id || '',
          veiculo_id: it.veiculo_id || ''
        }))
      : [];
    
    setItems(additionalItems);
    setEditingId(item.id);
    setCaError('');
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente excluir este abastecimento?')) return;
    try {
      await api.delete(`/abastecimentos/${id}`);
      toast.success('Abastecimento excluído com sucesso!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir abastecimento');
    }
  };

  // Duplicar abastecimento - copia dados e abre formulário para novo registro
  const handleDuplicate = (item) => {
    const func = funcionarios.find(f => f.id === item.funcionario_id);
    const secretariaId = func?.secretaria_id || '';
    const firstItem = item.items && item.items.length > 0 ? item.items[0] : null;
    
    setFormData({
      date: new Date().toISOString().split('T')[0], // Data de hoje
      secretaria_id: secretariaId,
      funcionario_id: item.funcionario_id,
      veiculo_id: item.veiculo_id,
      posto_id: item.posto_id,
      combustivel_id: firstItem?.combustivel_id || '',
      quantidade: firstItem?.quantity?.toString() || '',
      preco_unitario: firstItem?.price_per_liter?.toString() || '',
      odometer_start: item.odometer_end?.toString() || '', // Começa onde o anterior terminou
      odometer_end: '',
      codigo_abastecimento: '', // Novo codigo sera gerado
      comunidade_id: item.comunidade_id || '',
      motor_hp: item.motor_hp || '',
      observacoes: item.observacoes || '',
      servico: item.servico || ''
    });
    
    // Copiar itens adicionais
    const additionalItems = item.items && item.items.length > 1 
      ? item.items.slice(1).map(it => ({
          combustivel_id: it.combustivel_id || '',
          quantity: it.quantity?.toString() || '',
          price_per_liter: it.price_per_liter?.toString() || '',
          funcionario_id: it.funcionario_id || '',
          veiculo_id: it.veiculo_id || ''
        }))
      : [];
    
    setItems(additionalItems);
    setEditingId(null); // Nao e edicao, e um novo registro
    setDialogOpen(true);
    toast.info('Abastecimento duplicado! Ajuste os dados e salve.');
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      secretaria_id: '',
      funcionario_id: '',
      veiculo_id: '',
      posto_id: '',
      combustivel_id: '',
      quantidade: '',
      preco_unitario: '',
      odometer_start: '',
      odometer_end: '',
      codigo_abastecimento: '',
      comunidade_id: '',
      motor_hp: '',
      observacoes: '',
      servico: ''
    });
    setItems([]);
    setEditingId(null);
  };

  const openNewDialog = async () => {
    resetForm();
    setCaError('');
    // Buscar próximo CA automaticamente
    try {
      const res = await api.get('/abastecimentos/proximo-ca');
      setFormData(prev => ({ ...prev, codigo_abastecimento: res.data.proximo_ca }));
    } catch (err) {
      console.error('Erro ao buscar próximo CA:', err);
    }
    setDialogOpen(true);
  };

  const exportExcel = async () => {
    try {
      const response = await api.get('/reports/excel', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'relatorio_abastecimentos.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Relatório exportado com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar relatório');
    }
  };

  const printAbastecimento = async (id, ca) => {
    try {
      const response = await api.get(`/abastecimentos/${id}/print`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `requisicao_${ca}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Comprovante gerado com sucesso!');
    } catch (error) {
      toast.error('Erro ao gerar comprovante');
    }
  };

  const togglePrintSelect = (id) => {
    setSelectedForPrint(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const togglePrintSelectAll = () => {
    if (selectedForPrint.size === abastecimentosFiltrados.length) {
      setSelectedForPrint(new Set());
    } else {
      setSelectedForPrint(new Set(abastecimentosFiltrados.map(a => a.id)));
    }
  };

  const printBatch = async () => {
    if (selectedForPrint.size === 0) { toast.error('Selecione pelo menos uma requisição.'); return; }
    setPrintingBatch(true);
    try {
      const response = await api.post('/abastecimentos/print-batch', { ids: Array.from(selectedForPrint) }, { responseType: 'blob', timeout: 120000 });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `requisicoes_lote_${selectedForPrint.size}CAs.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`PDF com ${selectedForPrint.size} requisição(ões) gerado!`);
      setSelectedForPrint(new Set());
    } catch (error) {
      toast.error('Erro ao gerar PDF em lote.');
    } finally {
      setPrintingBatch(false);
    }
  };

  const toggleStatusContabil = async (itemId) => {
    try {
      const res = await api.patch(`/abastecimentos/${itemId}/toggle-status`);
      const newStatus = res.data.status_contabil;
      setAbastecimentos(prev => prev.map(a => a.id === itemId ? { ...a, status_contabil: newStatus } : a));
      toast.success(newStatus === 'verde' ? 'Contabilizado' : 'Desconsiderado');
    } catch (error) {
      toast.error('Erro ao alterar status');
    }
  };

  const getLookupValue = (id, array, field = 'name') => {
    const item = array.find(i => i.id === id);
    return item ? item[field] : 'N/A';
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#003366]"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="abastecimentos-page">
      {/* CSS para animação de piscar */}
      <style>{`
        @keyframes blink-saved {
          0%, 100% { background-color: transparent; }
          50% { background-color: #bbf7d0; }
        }
        .blink-saved-row {
          animation: blink-saved 0.5s ease-in-out 3;
        }
      `}</style>

      {/* Diálogo de confirmação para sair sem salvar */}
      <UnsavedChangesDialog
        isOpen={unsavedChanges.showDialog}
        onOpenChange={unsavedChanges.setShowDialog}
        onConfirmLeave={unsavedChanges.confirmLeave}
      />
      
      <div className="flex justify-between items-center sticky top-0 z-20 bg-[#F8FAFC] py-3 -mx-2 px-2 border-b border-transparent" style={{ backdropFilter: 'blur(8px)', backgroundColor: 'rgba(248,250,252,0.95)' }}>
        <div>
          <h2 className="text-3xl font-bold text-[#003366]" style={{ fontFamily: 'Manrope, sans-serif' }}>Abastecimentos</h2>
          <p className="text-slate-600">Registro e controle de abastecimentos da frota</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={exportExcel} variant="outline" data-testid="export-excel-button">
            <Download className="mr-2" size={20} />
            Exportar Excel
          </Button>
          <Dialog 
            open={dialogOpen} 
            onOpenChange={(open) => {
              if (!open && unsavedChanges.hasChanges) {
                unsavedChanges.tryAction(() => setDialogOpen(false));
              } else {
                setDialogOpen(open);
              }
            }}
          >
            <DialogTrigger asChild>
              <Button onClick={openNewDialog} className="bg-[#0066CC] hover:bg-[#0052A3]" data-testid="add-abastecimento-button">
                <Plus className="mr-2" size={20} />
                Novo Abastecimento
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingId ? 'Editar Abastecimento' : 'Novo Abastecimento'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="abastecimento-form">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="date">Data *</Label>
                    <Input
                      id="date"
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                      data-testid="abastecimento-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="codigo_abastecimento">Nº CA</Label>
                    <div className="relative">
                      <Input
                        id="codigo_abastecimento"
                        value={formData.codigo_abastecimento}
                        onChange={async (e) => {
                          const val = e.target.value;
                          setFormData({ ...formData, codigo_abastecimento: val });
                          setCaError('');
                          if (val.trim()) {
                            setCaChecking(true);
                            try {
                              const res = await api.get(`/abastecimentos/verificar-ca/${encodeURIComponent(val.trim())}${editingId ? `?excluir_id=${editingId}` : ''}`);
                              if (res.data.existe) {
                                setCaError('ESSE Nº DE CA JÁ EXISTE');
                              }
                            } catch (err) { /* ignore */ }
                            setCaChecking(false);
                          }
                        }}
                        placeholder="Gerado automaticamente"
                        className={caError ? 'border-red-500 focus:ring-red-500' : ''}
                        data-testid="abastecimento-ca-input"
                      />
                      {caChecking && <span className="absolute right-3 top-2.5 text-xs text-slate-400">Verificando...</span>}
                    </div>
                    {caError && (
                      <p className="text-xs text-red-600 font-bold" data-testid="ca-error-message">{caError}</p>
                    )}
                    {!caError && formData.codigo_abastecimento && (
                      <p className="text-xs text-green-600">Nº CA disponível</p>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="secretaria">Secretaria *</Label>
                    <select
                      id="secretaria"
                      value={formData.secretaria_id}
                      onChange={(e) => setFormData({ ...formData, secretaria_id: e.target.value, funcionario_id: '', veiculo_id: '' })}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      required
                      data-testid="abastecimento-secretaria-select"
                    >
                      <option value="">Selecione a Secretaria...</option>
                      {secretarias.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="funcionario">Funcionario Responsavel *</Label>
                    <select
                      id="funcionario"
                      value={formData.funcionario_id}
                      onChange={(e) => {
                        const funcId = e.target.value;
                        const lastVehicle = getLastUsedVehicle(funcId);
                        if (lastVehicle) {
                          console.log('Auto-preenchendo veiculo:', lastVehicle, 'para funcionario:', funcId);
                          setFormData(prev => ({ ...prev, funcionario_id: funcId, veiculo_id: lastVehicle }));
                        } else {
                          setFormData(prev => ({ ...prev, funcionario_id: funcId }));
                        }
                      }}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      required
                      disabled={!formData.secretaria_id}
                      data-testid="abastecimento-funcionario-select"
                    >
                      <option value="">{formData.secretaria_id ? 'Selecione...' : 'Selecione a secretaria primeiro'}</option>
                      {funcionariosFiltrados.map(f => {
                        const veiculoVinculado = f.veiculo_id ? veiculos.find(v => v.id === f.veiculo_id) : null;
                        return (
                          <option key={f.id} value={f.id}>
                            {f.name} {veiculoVinculado ? `(${veiculoVinculado.plate})` : ''}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="servico">SERVIÇO</Label>
                  <input
                    id="servico"
                    type="text"
                    value={formData.servico}
                    onChange={(e) => setFormData({ ...formData, servico: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    placeholder="Informe o serviço..."
                    data-testid="abastecimento-servico-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {isResgate ? (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="comunidade">Comunidade *</Label>
                        <select
                          id="comunidade"
                          value={formData.comunidade_id}
                          onChange={(e) => setFormData({ ...formData, comunidade_id: e.target.value })}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          required
                          data-testid="abastecimento-comunidade-select"
                        >
                          <option value="">Selecione a Comunidade...</option>
                          {comunidades.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="motor_hp">Motor/HP</Label>
                        <Input
                          id="motor_hp"
                          value={formData.motor_hp}
                          onChange={(e) => setFormData({ ...formData, motor_hp: e.target.value })}
                          placeholder="Ex: Motor 15HP"
                          data-testid="abastecimento-motor-hp-input"
                        />
                      </div>
                    </>
                  ) : (
                    <div className="space-y-2">
                      <Label htmlFor="veiculo">Veículo *</Label>
                      <select
                        id="veiculo"
                        value={formData.veiculo_id}
                        onChange={(e) => {
                          const veicId = e.target.value;
                          const updatedForm = { ...formData, veiculo_id: veicId };
                          // Auto-preencher combustível do veículo se tiver tipo definido
                          if (veicId) {
                            const veicSel = veiculos.find(v => v.id === veicId);
                            if (veicSel && veicSel.combustivel_tipo_id) {
                              updatedForm.combustivel_id = veicSel.combustivel_tipo_id;
                            }
                          }
                          setFormData(updatedForm);
                        }}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        required
                        disabled={!formData.secretaria_id}
                        data-testid="abastecimento-veiculo-select"
                      >
                        <option value="">{formData.secretaria_id ? 'Selecione...' : 'Selecione a secretaria primeiro'}</option>
                        {veiculosFiltrados.map(v => (
                          <option key={v.id} value={v.id}>{v.plate} - {v.description}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="posto">Posto *</Label>
                    <select
                      id="posto"
                      value={formData.posto_id}
                      onChange={(e) => handlePostoChange(e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      required
                      data-testid="abastecimento-posto-select"
                    >
                      <option value="">Selecione...</option>
                      {postos.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="combustivel">Tipo de Combustível *</Label>
                    <select
                      id="combustivel"
                      value={formData.combustivel_id}
                      onChange={(e) => handleCombustivelChange(e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      required
                      disabled={!formData.posto_id}
                      data-testid="abastecimento-combustivel-select"
                    >
                      <option value="">{formData.posto_id ? 'Selecione...' : 'Selecione o posto primeiro'}</option>
                      {combustiveisFiltrados.map(c => (
                        <option key={c.id} value={c.id}>{c.type} - R$ {(c.valor_litro || 0).toFixed(2)}/L</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="quantidade">Quantidade (L) *</Label>
                    <Input
                      id="quantidade"
                      type="number"
                      step="0.01"
                      value={formData.quantidade}
                      onChange={(e) => setFormData({ ...formData, quantidade: e.target.value })}
                      required
                      data-testid="abastecimento-quantidade-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="preco_unitario">Preço/Litro (R$) *</Label>
                    <Input
                      id="preco_unitario"
                      type="number"
                      step="0.01"
                      value={formData.preco_unitario}
                      onChange={(e) => setFormData({ ...formData, preco_unitario: e.target.value })}
                      required
                      data-testid="abastecimento-preco-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="odometer_start">Hodômetro Inicial *</Label>
                    {isResgate ? (
                      <span className="block text-sm text-slate-400 pt-2">N/A para SEMSA-RESGATE</span>
                    ) : (
                      <Input
                        id="odometer_start"
                        type="number"
                        step="0.1"
                        value={formData.odometer_start}
                        onChange={(e) => setFormData({ ...formData, odometer_start: e.target.value })}
                        required
                        data-testid="abastecimento-odometer-start-input"
                      />
                    )}
                  </div>
                </div>

                {!isResgate && (
                <div className="grid grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="odometer_end">Hodômetro Final *</Label>
                    <Input
                      id="odometer_end"
                      type="number"
                      step="0.1"
                      value={formData.odometer_end}
                      onChange={(e) => setFormData({ ...formData, odometer_end: e.target.value })}
                      required
                      data-testid="abastecimento-odometer-end-input"
                    />
                  </div>
                </div>
                )}

                <div className="border-t pt-4">
                  <div className="flex justify-between items-center mb-3">
                    <div>
                      <Label className="text-base font-semibold">Funcionários Adicionais</Label>
                      <p className="text-xs text-slate-500 mt-1">Opcional - Use apenas se houver mais funcionários neste abastecimento</p>
                    </div>
                    <Button type="button" onClick={addItem} size="sm" className="bg-green-600 hover:bg-green-700" disabled={!formData.secretaria_id}>
                      <Plus size={16} className="mr-1" /> Adicionar Funcionário
                    </Button>
                  </div>
                  
                  {!formData.secretaria_id && (
                    <div className="text-center py-4 text-slate-500 border rounded-lg bg-slate-50">
                      Selecione uma secretaria para adicionar funcionários
                    </div>
                  )}
                  
                  {formData.secretaria_id && items.length > 0 && items.map((item, index) => (
                    <div key={index} className="border rounded-lg p-4 mb-3 bg-slate-50">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium text-sm">Funcionário Adicional {index + 1}</span>
                        <Button type="button" onClick={() => removeItem(index)} size="sm" variant="destructive">
                          <X size={16} />
                        </Button>
                      </div>
                      <div className="grid grid-cols-3 gap-3 mb-3">
                        <div className="space-y-2">
                          <Label>Funcionário *</Label>
                          <select
                            value={item.funcionario_id || ''}
                            onChange={(e) => {
                              const funcId = e.target.value;
                              const newItems = [...items];
                              newItems[index] = { ...newItems[index], funcionario_id: funcId };
                              // Auto-preencher veículo usando histórico
                              const lastVehicle = getLastUsedVehicle(funcId);
                              if (lastVehicle) {
                                newItems[index].veiculo_id = lastVehicle;
                              } else {
                                newItems[index].veiculo_id = '';
                              }
                              setItems(newItems);
                            }}
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            required
                          >
                            <option value="">Selecione...</option>
                            {funcionariosFiltrados.map(f => {
                              const veiculoVinculado = f.veiculo_id ? veiculos.find(v => v.id === f.veiculo_id) : null;
                              return (
                                <option key={f.id} value={f.id}>
                                  {f.name} {veiculoVinculado ? `(${veiculoVinculado.plate})` : ''}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                        <div className="space-y-2">
                          <Label>Veículo *</Label>
                          <select
                            value={item.veiculo_id || ''}
                            onChange={(e) => updateItem(index, 'veiculo_id', e.target.value)}
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            required
                          >
                            <option value="">Selecione...</option>
                            {(() => {
                              const itemFunc = item.funcionario_id ? funcionarios.find(f => f.id === item.funcionario_id) : null;
                              const secId = itemFunc ? itemFunc.secretaria_id : formData.secretaria_id;
                              return veiculos.filter(v => 
                                v.secretaria_id === secId || 
                                v.secretaria_id === formData.secretaria_id ||
                                (itemFunc && v.id === itemFunc.veiculo_id) ||
                                (item.veiculo_id && v.id === item.veiculo_id)
                              ).map(v => (
                                <option key={v.id} value={v.id}>{v.plate} - {v.description}</option>
                              ));
                            })()}
                          </select>
                        </div>
                        <div className="space-y-2">
                          <Label>Combustível *</Label>
                          <select
                            value={item.combustivel_id}
                            onChange={(e) => {
                              const comb = combustiveis.find(c => c.id === e.target.value);
                              updateItem(index, 'combustivel_id', e.target.value);
                              if (comb?.valor_litro) {
                                updateItem(index, 'price_per_liter', comb.valor_litro.toString());
                              }
                            }}
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            required
                            disabled={!formData.posto_id}
                          >
                            <option value="">{formData.posto_id ? 'Selecione...' : 'Selecione o posto'}</option>
                            {combustiveisFiltrados.map(c => (
                              <option key={c.id} value={c.id}>{c.type} - R$ {(c.valor_litro || 0).toFixed(2)}/L</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                          <Label>Quantidade (L) *</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={item.quantity}
                            onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Preço/Litro (R$) *</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={item.price_per_liter}
                            onChange={(e) => updateItem(index, 'price_per_liter', e.target.value)}
                            required
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Observações */}
                <div className="space-y-2">
                  <Label htmlFor="observacoes">DEMANDA</Label>
                  <textarea
                    id="observacoes"
                    value={formData.observacoes}
                    onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                    placeholder="Demanda (opcional)"
                    rows={2}
                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    data-testid="abastecimento-obs-input"
                  />
                </div>

                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                  <Button type="submit" className="bg-[#003366] hover:bg-[#002244]" data-testid="abastecimento-submit-button">Salvar</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Lista de Abastecimentos</CardTitle>
          <MonthFilter
            selectedMonth={selectedMonth}
            onChange={setSelectedMonth}
          />
        </CardHeader>
        <CardContent>
          {/* Barra de pesquisa */}
          <div className="mb-3 relative max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              type="text"
              placeholder="Pesquisar por CA, data (dd/mm/aaaa) ou secretaria..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="search-abastecimentos"
            />
          </div>

          {/* Resumo detalhado */}
          {(() => {
            const verdes = abastecimentosFiltrados.filter(a => a.status_contabil !== 'vermelho');
            const vermelhos = abastecimentosFiltrados.filter(a => a.status_contabil === 'vermelho');
            const totalVerde = verdes.reduce((acc, a) => acc + (a.total_value || 0), 0);
            const totalVermelho = vermelhos.reduce((acc, a) => acc + (a.total_value || 0), 0);
            const litrosVerde = verdes.reduce((acc, a) => acc + (a.total_quantity || 0), 0);
            const litrosVermelho = vermelhos.reduce((acc, a) => acc + (a.total_quantity || 0), 0);

            // Litros desconsiderados por tipo de combustível
            const litrosDesc = {};
            vermelhos.forEach(a => {
              (a.items || []).forEach(it => {
                const comb = combustiveis.find(c => c.id === it.combustivel_id);
                if (comb) {
                  const tipo = comb.type.toUpperCase();
                  let cat = null;
                  if (tipo.includes('DIESEL') && tipo.includes('S-10') || tipo.includes('S10')) cat = 'DIESEL S10';
                  else if (tipo.includes('DIESEL')) cat = 'DIESEL COMUM';
                  else if (tipo.includes('GASOLINA')) cat = 'GASOLINA';
                  if (cat) {
                    litrosDesc[cat] = (litrosDesc[cat] || 0) + (it.quantity || 0);
                  }
                }
              });
            });

            const contextLabel = searchTerm.trim()
              ? <span className="text-sm text-slate-600">Pesquisando em <strong>todos os meses</strong> — {abastecimentosFiltrados.length} resultado(s)</span>
              : selectedMonth
              ? <span className="text-sm text-slate-600">Exibindo <strong>{abastecimentosFiltrados.length}</strong> abastecimentos do mês selecionado</span>
              : <span className="text-sm text-slate-600">Exibindo <strong>todos</strong> os abastecimentos ({abastecimentosFiltrados.length})</span>;

            return (
              <div className="mb-4 p-4 bg-slate-50 border rounded-lg space-y-3" data-testid="resumo-panel">
                <div className="flex items-center justify-between">
                  {contextLabel}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-white border border-green-200 rounded-lg p-3 text-center">
                    <p className="text-xs text-green-600 font-medium">TOTAL CONTABILIZADO</p>
                    <p className="text-lg font-bold text-green-700" data-testid="total-verde">
                      R$ {totalVerde.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                    <p className="text-xs text-green-500">{verdes.length} registro(s) | {litrosVerde.toLocaleString('pt-BR', { minimumFractionDigits: 1 })} L</p>
                  </div>
                  <div className="bg-white border border-red-200 rounded-lg p-3 text-center">
                    <p className="text-xs text-red-600 font-medium">TOTAL DESCONSIDERADO</p>
                    <p className="text-lg font-bold text-red-600" data-testid="total-vermelho">
                      {totalVermelho > 0 ? `-R$ ${totalVermelho.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : 'R$ 0,00'}
                    </p>
                    <p className="text-xs text-red-400">{vermelhos.length} registro(s) | {litrosVermelho > 0 ? `-${litrosVermelho.toLocaleString('pt-BR', { minimumFractionDigits: 1 })} L` : '0,0 L'}</p>
                  </div>
                  {['DIESEL COMUM', 'DIESEL S10', 'GASOLINA'].map(cat => (
                    <div key={cat} className="bg-white border border-red-100 rounded-lg p-3 text-center">
                      <p className="text-xs text-red-500 font-medium">{cat} DESC.</p>
                      <p className={`text-lg font-bold ${(litrosDesc[cat] || 0) > 0 ? 'text-red-600' : 'text-slate-400'}`} data-testid={`litros-desc-${cat.toLowerCase().replace(/\s/g, '-')}`}>
                        {(litrosDesc[cat] || 0) > 0 ? `-${(litrosDesc[cat] || 0).toLocaleString('pt-BR', { minimumFractionDigits: 1 })} L` : '0,0 L'}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
          {/* Barra de impressão em lote */}
          {selectedForPrint.size > 0 && (
            <div className="mb-3 flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="batch-print-bar">
              <span className="text-sm font-medium text-blue-800">
                {selectedForPrint.size} requisição(ões) selecionada(s)
              </span>
              <Button
                size="sm"
                onClick={printBatch}
                disabled={printingBatch}
                className="bg-[#003366] hover:bg-[#002244] text-white"
                data-testid="print-batch-btn"
              >
                {printingBatch
                  ? <><Loader2 className="animate-spin mr-1" size={14} /> Gerando...</>
                  : <><Printer size={14} className="mr-1" /> Imprimir {selectedForPrint.size} Selecionada(s)</>
                }
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelectedForPrint(new Set())} className="text-xs text-gray-500">
                Limpar seleção
              </Button>
            </div>
          )}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">
                    <button onClick={togglePrintSelectAll} className="p-0.5" data-testid="select-all-print-btn" title={selectedForPrint.size === abastecimentosFiltrados.length ? 'Desmarcar todos' : 'Selecionar todos'}>
                      {selectedForPrint.size === abastecimentosFiltrados.length && abastecimentosFiltrados.length > 0
                        ? <CheckSquare size={16} className="text-blue-600" />
                        : <Square size={16} className="text-gray-400" />
                      }
                    </button>
                  </TableHead>
                  <TableHead>CA</TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Funcionário</TableHead>
                  <TableHead>Secretaria</TableHead>
                  <TableHead>Posto</TableHead>
                  <TableHead>Itens</TableHead>
                  <TableHead>Valor Total</TableHead>
                  <TableHead>Km Rodados</TableHead>
                  <TableHead>Média</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...abastecimentosFiltrados].sort((a, b) => {
                  // Ordenação crescente pelo codigo_abastecimento (numérico)
                  const numA = parseInt(a.codigo_abastecimento) || 0;
                  const numB = parseInt(b.codigo_abastecimento) || 0;
                  return numA - numB;
                }).map((item) => {
                  // Buscar o FUNCIONÁRIO para pegar a secretaria (baseado no CA)
                  const funcionario = funcionarios.find(f => f.id === item.funcionario_id);
                  const secretariaId = funcionario?.secretaria_id;
                  const secretariaNome = secretariaId ? getLookupValue(secretariaId, secretarias) : '-';
                  
                  // Buscar o posto para aplicar a cor
                  const posto = postos.find(p => p.id === item.posto_id);
                  const postoNome = posto?.name || '-';
                  const postoColor = POSTO_COLORS[postoNome] || { bg: '#003366', text: 'white' };
                  
                  const isVermelho = item.status_contabil === 'vermelho';
                  
                  return (
                  <TableRow key={item.id} className={`table-row ${highlightId === item.id ? 'blink-saved-row' : ''} ${isVermelho ? 'opacity-40' : ''}`} data-testid={`abastecimento-row-${item.id}`}>
                    <TableCell className="w-8">
                      <button onClick={() => togglePrintSelect(item.id)} className="p-0.5" data-testid={`select-print-${item.id}`}>
                        {selectedForPrint.has(item.id)
                          ? <CheckSquare size={16} className="text-blue-600" />
                          : <Square size={16} className="text-gray-300" />
                        }
                      </button>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleStatusContabil(item.id)}
                          className={`flex-shrink-0 rounded-full p-0.5 transition-colors ${isVermelho ? 'text-red-500 hover:text-green-500' : 'text-green-500 hover:text-red-500'}`}
                          title={isVermelho ? 'Clique para Contabilizar' : 'Clique para Desconsiderar'}
                          data-testid={`toggle-status-${item.id}`}
                        >
                          {isVermelho ? <XCircle size={20} /> : <CheckCircle2 size={20} />}
                        </button>
                        <span className="font-bold text-[#CC0000]">{(item.codigo_abastecimento || 'N/A').toString().toUpperCase()}</span>
                      </div>
                    </TableCell>
                    <TableCell>{item.date ? item.date.split('-').reverse().join('/') : '-'}</TableCell>
                    <TableCell className="uppercase">{getLookupValue(item.funcionario_id, funcionarios)}</TableCell>
                    <TableCell className="font-medium uppercase">{secretariaNome}</TableCell>
                    <TableCell>
                      <span 
                        className="font-semibold"
                        style={{ color: postoColor.bg }}
                      >
                        {postoNome.toUpperCase()}
                      </span>
                    </TableCell>
                    <TableCell>{item.items?.length || 0} item(ns)</TableCell>
                    <TableCell className="font-medium text-green-600">R$ {item.total_value?.toFixed(2) || '0.00'}</TableCell>
                    <TableCell>{item.km_driven?.toFixed(1) || '0.0'} km</TableCell>
                    <TableCell>{item.avg_consumption?.toFixed(2) || '0.00'} km/L</TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-2 justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => printAbastecimento(item.id, item.codigo_abastecimento)}
                          className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                          data-testid={`print-abastecimento-${item.id}`}
                          title="Imprimir Comprovante"
                        >
                          <Printer size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDuplicate(item)}
                          className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                          data-testid={`duplicate-abastecimento-${item.id}`}
                          title="Duplicar Abastecimento"
                        >
                          <Copy size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(item)}
                          data-testid={`edit-abastecimento-${item.id}`}
                          title="Editar"
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(item.id)}
                          data-testid={`delete-abastecimento-${item.id}`}
                          title="Excluir"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
                {abastecimentos.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-slate-500">
                      Nenhum abastecimento registrado
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
